import { EVENT_NAMES } from './events'
import { getActiveCommunity } from '../activeCommunity'

export { EVENTS } from './events'

// No token means the module does nothing at all, so local dev and tests stay silent and there is no
// environment-name logic to get wrong. Each environment supplies its own project token.
const TOKEN = process.env.REACT_APP_MIXPANEL_TOKEN

// Points at Mixpanel directly until the first-party relay exists. Switching to it is then a config
// change rather than a code change. Ad blockers drop a large share of direct traffic, so this must
// be set before we trust production numbers.
const API_HOST = process.env.REACT_APP_MIXPANEL_API_HOST

const IS_PRODUCTION = process.env.NODE_ENV === 'production'

// Properties carry ids, counts and enums -- never names, emails, phone numbers or free text. Keys
// are checked rather than values because a key is a stable thing to reason about, and `community_name`
// is as much free text as `email` is.
const FORBIDDEN_KEY = /name|email|phone|address|birthdate|username|password/i

const MAX_QUEUED_CALLS = 20

let client = null
let loader = null
const queued = []

const superProperties = () => ({
  platform: 'web',
  environment: process.env.REACT_APP_ENVIRONMENT || 'development',
  ...(process.env.REACT_APP_VERSION && { app_release: process.env.REACT_APP_VERSION }),
})

// Loaded on demand so the SDK adds nothing to first paint. Calls made before it arrives are queued,
// up to a cap -- if something is firing hundreds of events before load, dropping them is the lesser
// problem.
const load = () => {
  if (!TOKEN) { return Promise.resolve(null) }
  if (loader) { return loader }

  loader = import('mixpanel-browser')
    .then(({ default: mixpanel }) => {
      mixpanel.init(TOKEN, {
        ...(API_HOST && { api_host: API_HOST }),
        autocapture: false, // a plan turns into noise, and every captured click is billed
        track_pageview: false,
        persistence: 'localStorage',
        record_sessions_percent: 0, // session replay stays off: minors use this product
        debug: !IS_PRODUCTION,
      })
      mixpanel.register(superProperties())

      client = mixpanel
      queued.splice(0).forEach((call) => call(mixpanel))

      return mixpanel
    })
    .catch(() => null) // analytics must never break the app

  return loader
}

const withClient = (call) => {
  if (!TOKEN) { return }
  if (client) { call(client); return }
  if (queued.length < MAX_QUEUED_CALLS) { queued.push(call) }

  load()
}

const reject = (message) => {
  // Loud in development and tests so it is caught while writing the code, silent in production so a
  // bad call loses one event rather than breaking a page.
  if (!IS_PRODUCTION) { throw new Error(`[analytics] ${message}`) }

  console.warn(`[analytics] ${message}`) // eslint-disable-line no-console
}

const sanitize = (properties) => (
  Object.entries(properties).reduce((kept, [key, value]) => {
    if (FORBIDDEN_KEY.test(key)) {
      reject(`"${key}" looks like personal data. Send an id instead.`)
      return kept
    }

    // Mixpanel treats null and '' as values worth storing; leaving the property out is cleaner.
    if (value === null || value === undefined || value === '') { return kept }

    return { ...kept, [key]: value }
  }, {})
)

export const initAnalytics = () => { load() }

// Call on sign-in and on every load while already signed in. The database id, never the email --
// it is the one identifier shared with the backend and the mobile apps, so one person stays one
// profile. people.set must follow identify, or Mixpanel's Users page stays empty.
export const identifyUser = (user) => {
  if (!user?.id) { return }

  // The active community, not communities[0] -- a coach can belong to several and the array order is
  // whatever the API returned, so communities[0] would attribute them somewhere arbitrary.
  const community = getActiveCommunity(user)

  // Sanitized at the call site, not inside the queued call, so a bad property throws where the stack
  // trace points at the offending code.
  const profile = sanitize({
    user_type: user.type,
    family_admin: user.family_admin,
    account_segment: user.account_segment,
    community_id: community?.id,
    license_type: community?.license_type,
    community_count: user.communities?.length,
    locale: user.locale,
    $created: user.created_at,
  })

  withClient((mixpanel) => {
    mixpanel.identify(String(user.id))
    mixpanel.people.set(profile)
    // Also a super property: the profile only holds where they are now, so without this an event
    // cannot be attributed to the community it happened in.
    mixpanel.register(sanitize({ community_id: community?.id }))
  })
}

export const trackEvent = (name, properties = {}) => {
  if (!EVENT_NAMES.includes(name)) {
    reject(`"${name}" is not in the event allowlist. Add it to utility/analytics/events.js first.`)
    return
  }

  const clean = sanitize(properties)

  withClient((mixpanel) => mixpanel.track(name, clean))
}

// Must run on any confirmed signed-out state, not only the logout button: without it the next
// anonymous visitor on this browser is credited to the previous user. Shared family devices make
// that a routine case for us rather than an edge one.
export const resetAnalytics = () => {
  withClient((mixpanel) => mixpanel.reset())
}
